/** The record editor's `opening_hours` widget: the week (`OpeningHours`) on a FieldSlot.
 *
 *  ONE error caption: the widget owns its errors (`owns: 'errors'`). A half-typed time and
 *  a broken rule are captioned on the day they concern; the server's errors and the
 *  preview's conflicts under the week, with a conflict's suggested fix. The slot's caption
 *  shows none of them — it keeps the "was …" line. The rule still blocks the save through
 *  `checkValue` (the ledger names it too), and a half-typed time through `onInvalid`.
 *
 *  "Copy to all days" is ONE undo step of its own: the slot's focus session is closed
 *  around it (`onBlur` … `onFocus`), so it never folds into the typing before or after.
 *
 *  View mode shows the same seven rows in words (`View`), so pressing E moves nothing. */
import type { RecordField, RecordWidget, RecordWidgetProps } from '../../editor/types';
import { useRecordEditor } from '../../editor/context';
import { ConflictFixes } from '../ConflictFixes';

import { OpeningHours } from './OpeningHours';
import { formatWeekHours, openingHoursError } from './openingHoursModel';

function OpeningHoursInput(p: RecordWidgetProps) {
  const { conflictsFor, pathOf } = useRecordEditor();
  const path = pathOf(p.field);
  const server = [...new Set(conflictsFor(p.rec, path).map((c) => c.message))];
  return (
    <>
      <OpeningHours
        id={p.id}
        value={p.value}
        onChange={(v, opts) => {
          if (!opts?.ownStep) {
            p.onChange(v);
            return;
          }
          p.onBlur();
          p.onChange(v, { label: opts.label });
          p.onFocus();
        }}
        onInvalid={p.onInvalid}
        onFocus={p.onFocus}
        onBlur={p.onBlur}
        disabled={p.disabled}
        labelledBy={p.labelId}
        describedBy={p.describedBy}
        error={server.length > 0 ? server.join(' · ') : null}
      />
      <ConflictFixes rec={p.rec} path={path} />
    </>
  );
}

const noop = () => {};

/** The stored week read-only: the edit mode's seven rows, in words. */
export function OpeningHoursView({ field, value }: { field: RecordField; value: unknown }) {
  return <OpeningHours readOnly value={value} onChange={noop} label={field.label} />;
}

export const OpeningHoursWidget: RecordWidget = Object.assign(OpeningHoursInput, {
  formatChange: formatWeekHours,
  checkValue: (to: unknown) => openingHoursError(to),
  owns: 'errors' as const,
  View: OpeningHoursView,
});
